import {css, StyleSheet} from 'aphrodite';
import React, {useState, useRef, useEffect, useCallback, useMemo} from 'react';

import {trackEvent} from 'analytics/Analytics';
import {ELN_ANALYTICS_ACTIONS} from 'constants/analytics-products';
import {getInsufficientCreditsAgentTypeError} from 'llm/chat-library/MeteringUtils';
import LLMLoadingMessage from 'llm/loading/LLMLoadingMessage';
import * as ToastActions from 'nav/toasts/ToastActions';
import {ToastType} from 'nav/toasts/ToastConstants';
import {AddStructuredTableRowsUsingAIResult} from 'notebook/ai-tools/flexible-data-entry/addStructuredTableRowsUsingAI';
import {FlexibleDataEntryExperience} from 'notebook/ai-tools/NotebookAIToolsConstants';
import {
  fillStructuredTableUsingAIDirect,
  FillStructuredTableUsingAIResponse,
} from 'notebook/ai-tools/NotebookAIToolsDAO';
import {waitForEntryOrTemplateSaved} from 'notebook/entries/BaseEntryUtil';
import {StructuredTable} from 'notebook/structured-tables/StructuredTableConstants';
import {getEntryOrFileIdKey} from 'notebook/util/analytics/FileAnalyticsUtils';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {GRAY_LIGHTER, GRAY_DARK, RED} from 'ui-library/Colors';
import {FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import useIsNotUnmounted from 'ui-library/hooks/useIsNotUnmounted';
import {getUserMessage, isJqXHR} from 'util/Net';

const DEFAULT_STATUS_MESSAGE = 'AI can make mistakes.\nAlways check results.';
export const LARGE_OUTPUT_TOAST_MESSAGE =
  'Your requested output was too large. Please try again using the Data Entry Agent modal by clicking on AI Tools and then Insert Data.';

// Matches the non-user-facing error field from benchling.lib.flask_web_timeout.handle_timeout
const FLASK_TIMEOUT_ERROR = 'Request Timed Out';

function isTimeoutError(error: unknown): boolean {
  return isJqXHR(error) && error.responseJSON?.error === FLASK_TIMEOUT_ERROR;
}

interface AIDataEntryPopoverProps {
  entryId: string;
  structuredTable: StructuredTable<unknown, unknown>;
  onSubmit: (result: AddStructuredTableRowsUsingAIResult | null) => void;
  onCancel: () => void;
  onUndo: () => void;
  hasSuccessfulSubmission: boolean;
  setHasSuccessfulSubmission: (hasSuccessfulSubmission: boolean) => void;
  addAIDataEntryRows: (
    initialPromptText?: string,
    initialSelectedTableName?: string,
    result?: AddStructuredTableRowsUsingAIResult | null
  ) => void;
}

export default function AIDataEntryPopover({
  entryId,
  structuredTable,
  onSubmit,
  onCancel,
  onUndo,
  hasSuccessfulSubmission,
  setHasSuccessfulSubmission,
  addAIDataEntryRows,
}: AIDataEntryPopoverProps): JSX.Element {
  const [prompt, setPrompt] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);

  const [abortController, setAbortController] = useState<AbortController | null>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const isMounted = useIsNotUnmounted();
  const [errorMessage, setErrorMessage] = useState<{
    level: InlineBannerType;
    title: string;
    detail: string | null;
  } | null>(null);
  const fileIdKey = useMemo(() => getEntryOrFileIdKey(entryId, true), [entryId]);

  useEffect(() => {
    // Focus the input when the popover opens
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  const handleStop = useCallback(() => {
    if (abortController) {
      abortController.abort();
    }
    setIsProcessing(false);
    setHasSuccessfulSubmission(false);
  }, [abortController, setHasSuccessfulSubmission]);

  const handleUndo = useCallback(() => {
    trackEvent(ELN_ANALYTICS_ACTIONS.STARTED_OVER_FLEXIBLE_DATA_ENTRY, {
      wasSuccessful: hasSuccessfulSubmission,
      wasVerified: null,
      numFiles: 0,
      entryId,
      experience: FlexibleDataEntryExperience.POPOVER,
    });
    onUndo();
    setHasSuccessfulSubmission(false);
  }, [entryId, hasSuccessfulSubmission, onUndo, setHasSuccessfulSubmission]);

  const handleAddMoreRows = useCallback(() => {
    trackEvent(ELN_ANALYTICS_ACTIONS.ACCEPTED_FLEXIBLE_DATA_ENTRY_RESULT, {
      numFiles: 0,
      wasVerified: null,
      entryId,
      experience: FlexibleDataEntryExperience.POPOVER,
      isMultiTable: false,
    });
    setHasSuccessfulSubmission(false);
    setPrompt('');
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, [entryId, setHasSuccessfulSubmission]);

  useEffect(() => {
    trackEvent(ELN_ANALYTICS_ACTIONS.OPENED_FLEXIBLE_DATA_ENTRY_MODAL, {
      [fileIdKey]: entryId,
      experience: FlexibleDataEntryExperience.POPOVER,
    });
  }, [entryId, fileIdKey]);

  const handleSubmit = useCallback(async (): Promise<void> => {
    if (!prompt.trim() || isProcessing) {
      return;
    }

    setIsProcessing(true);
    setHasSuccessfulSubmission(false);
    setErrorMessage(null);

    // If this is a retry (same prompt), undo first
    if (hasSuccessfulSubmission) {
      trackEvent(ELN_ANALYTICS_ACTIONS.RETRIED_FLEXIBLE_DATA_ENTRY, {
        [fileIdKey]: entryId,
        experience: FlexibleDataEntryExperience.POPOVER,
      });
      onUndo();
      await waitForEntryOrTemplateSaved(entryId, 500, 30);
    }

    const controller = new AbortController();
    setAbortController(controller);

    const startTime = performance.now();
    trackEvent(ELN_ANALYTICS_ACTIONS.SUBMITTED_FLEXIBLE_DATA_ENTRY_REQUEST, {
      numFiles: 0,
      entryId,
      experience: FlexibleDataEntryExperience.POPOVER,
      isMultiTable: false,
    });

    const handleResponse = (response: FillStructuredTableUsingAIResponse): void => {
      if (!isMounted.current || controller.signal.aborted) {
        return;
      }

      const selectedResult = response.runResults[0];
      const wasSuccessful =
        selectedResult?.newRows != null && selectedResult.newRows[structuredTable.name]?.length > 0;
      trackEvent(ELN_ANALYTICS_ACTIONS.RECEIVED_FLEXIBLE_DATA_ENTRY_RESULT, {
        isModalOpen: isMounted.current,
        wasSuccessful,
        wasVerified: null,
        numFiles: 0,
        entryId,
        durationSeconds: (performance.now() - startTime) / 1000,
        experience: FlexibleDataEntryExperience.POPOVER,
        isMultiTable: false,
      });
      if (wasSuccessful) {
        // Automatically insert the rows
        const newRows = selectedResult.newRows[structuredTable.name].map((row) =>
          row.map((cell) => ({text: cell.text}))
        );
        if (newRows.length > 0) {
          setHasSuccessfulSubmission(true);
          setIsProcessing(false);
          onSubmit({newRows: selectedResult.newRows});
        } else {
          setHasSuccessfulSubmission(false);
          setIsProcessing(false);
          setErrorMessage({
            level: InlineBannerType.ERROR,
            title: 'No rows were added. Please try again.',
            detail: null,
          });
        }
      } else {
        setHasSuccessfulSubmission(false);
        setIsProcessing(false);
      }
    };

    try {
      const fullResponse = await fillStructuredTableUsingAIDirect(
        entryId,
        [structuredTable.name],
        [], // No files for popover version
        prompt.trim()
      );
      handleResponse(fullResponse);
      const responseWasSuccessful =
        fullResponse?.runResults.some((result) => result.newRows?.[structuredTable.name]?.length > 0) ??
        false;

      if (!responseWasSuccessful) {
        setHasSuccessfulSubmission(false);
        setIsProcessing(false);
      }
    } catch (e) {
      if (!isMounted.current || controller.signal.aborted) {
        return;
      }
      const creditError = getInsufficientCreditsAgentTypeError(e);
      if (creditError) {
        setErrorMessage({
          level: InlineBannerType.WARNING,
          title: creditError.title,
          detail: creditError.detail,
        });
        e.__suppress_notification = true; // no default toast: banner captures all relevant info.
      } else if (isTimeoutError(e)) {
        ToastActions.pushToast({
          message: LARGE_OUTPUT_TOAST_MESSAGE,
          type: ToastType.DANGER,
        });
        setErrorMessage({level: InlineBannerType.ERROR, title: LARGE_OUTPUT_TOAST_MESSAGE, detail: null});
        e.__suppress_notification = true; // no default toast
      } else {
        const errorMessage = getUserMessage(e) ?? 'An error occurred.';
        setErrorMessage({level: InlineBannerType.ERROR, title: errorMessage, detail: null});
      }
      setIsProcessing(false);

      // Re-throw the error to ensure it's properly handled upstream
      throw e;
    }

    setAbortController(null);
  }, [
    prompt,
    isProcessing,
    setHasSuccessfulSubmission,
    hasSuccessfulSubmission,
    entryId,
    fileIdKey,
    onUndo,
    isMounted,
    structuredTable.name,
    onSubmit,
  ]);

  const handleUploadFile = useCallback((): void => {
    onCancel();
    trackEvent(ELN_ANALYTICS_ACTIONS.OPEN_MODAL_FROM_FLEXIBLE_DATA_ENTRY_POPOVER, {
      [fileIdKey]: entryId,
    });
    addAIDataEntryRows(prompt.trim(), structuredTable.name);
  }, [onCancel, prompt, structuredTable.name, addAIDataEntryRows, entryId, fileIdKey]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent): void => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSubmit();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        onCancel();
      }
    },
    [onCancel, handleSubmit]
  );

  const isSubmitDisabled = !prompt.trim();
  const isInputDisabled = isProcessing;

  return (
    <div className={css(styles.container)}>
      <div className={css(styles.inputSection, isInputDisabled && styles.inputSectionDisabled)}>
        <textarea
          ref={inputRef}
          value={prompt}
          onChange={(e) => {
            setPrompt(e.target.value);
          }}
          onKeyDown={handleKeyDown}
          placeholder="Describe the rows you want to add..."
          className={css(styles.textarea)}
          rows={2}
          disabled={isInputDisabled}
          data-test-component="AIDataEntryPopover"
          data-test-element="promptInput"
        />
        <div className={css(styles.buttonSection)}>
          <div className={css(styles.leftButtons)}>
            <Tooltip text="Attach files" placement={FloatingPlacement.TOP}>
              <Button
                buttonStyle={ButtonStyle.TERTIARY_GRAY}
                size={ButtonSize.MINI}
                icon="paperclip"
                onClick={handleUploadFile}
                disabled={isInputDisabled}
                dataTestAttrs={{
                  component: 'AIDataEntryPopover',
                  element: 'uploadFileButton',
                }}
              />
            </Tooltip>
          </div>
          <div className={css(styles.rightButtons)}>
            {isProcessing && (
              <Tooltip text="Stop" placement={FloatingPlacement.TOP}>
                <Button
                  buttonStyle={ButtonStyle.PRIMARY}
                  size={ButtonSize.MINI}
                  icon="stop"
                  onClick={handleStop}
                  dataTestAttrs={{
                    component: 'AIDataEntryPopover',
                    element: 'stopButton',
                  }}
                />
              </Tooltip>
            )}
            {!isProcessing && (
              <Tooltip text={hasSuccessfulSubmission ? 'Retry' : 'Submit'} placement={FloatingPlacement.TOP}>
                <Button
                  buttonStyle={ButtonStyle.PRIMARY}
                  size={ButtonSize.MINI}
                  icon={hasSuccessfulSubmission ? 'arrows-rotate' : 'arrow-up'}
                  onClick={handleSubmit}
                  disabled={isSubmitDisabled}
                  dataTestAttrs={{
                    component: 'AIDataEntryPopover',
                    element: 'submitButton',
                  }}
                />
              </Tooltip>
            )}
          </div>
        </div>
      </div>
      {hasSuccessfulSubmission && (
        <div className={css(styles.statusSection)}>
          <div className={css(styles.leftActionButtons)}>
            <Button buttonStyle={ButtonStyle.LINK} onClick={handleUndo} disabled={isProcessing} text="Undo" />
          </div>
          <div className={css(styles.rightActionButtons)}>
            <Button
              buttonStyle={ButtonStyle.LINK}
              onClick={onCancel}
              disabled={isProcessing}
              text="Done"
              dataTestAttrs={{
                component: 'AIDataEntryPopover',
                element: 'doneButton',
              }}
            />
            <Button
              buttonStyle={ButtonStyle.TERTIARY_BLUE}
              size={ButtonSize.MINI}
              onClick={handleAddMoreRows}
              disabled={isProcessing}
              text="Add more rows"
              textClassName={css(styles.addMoreRowsText)}
            />
          </div>
        </div>
      )}
      {isProcessing && <LLMLoadingMessage text="Reading your prompt.." />}
      {!isProcessing && (
        <InlineBanner
          bannerType={errorMessage?.level ?? InlineBannerType.INFO}
          supportingInfo={
            <span>
              {/*

                Avoid using `text` prop: Popover is narrow (325px), so wrapping `text` looks bad, e.g.:

                    **This is the header that
                    wraps.**
                    And then the supporting
                    details follow.

                Instead, render as bold, then the actual detail text inline, e.g.:

                    **This is the header that
                    wraps.** And then the
                    supporting details follow.
               */}
              <strong>{errorMessage?.title ?? DEFAULT_STATUS_MESSAGE} </strong>
              <span>{errorMessage?.detail}</span>
            </span>
          }
          hideBackground={true}
        />
      )}
    </div>
  );
}

const styles = StyleSheet.create({
  container: {
    width: '300px',
    padding: '5px',
  },
  inputSection: {
    marginBottom: '8px',
    border: `1px solid ${GRAY_LIGHTER}`,
    borderRadius: '8px',
    padding: '8px',
    backgroundColor: 'white',
  },
  inputSectionDisabled: {
    backgroundColor: GRAY_LIGHTER,
  },
  textarea: {
    width: '100%',
    minHeight: '60px',
    padding: '0px',
    border: 'none',
    borderRadius: '4px',
    fontSize: '13px',
    fontFamily: 'inherit',
    resize: 'none',
    outline: 'none',
    backgroundColor: 'white',
    ':focus': {
      outline: 'none',
    },
    ':disabled': {
      backgroundColor: GRAY_LIGHTER,
      cursor: 'not-allowed',
    },
  },
  statusSection: {
    margin: '4px',
    padding: '4px 0',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    verticalAlign: 'middle',
    height: '30px',
  },
  status: {
    fontSize: '12px',
    color: GRAY_DARK,
  },
  statusError: {
    fontWeight: 'bold',
    color: RED,
  },
  buttonSection: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  leftButtons: {
    display: 'flex',
    gap: '8px',
  },
  rightButtons: {
    display: 'flex',
    gap: '8px',
  },
  leftActionButtons: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
  },
  rightActionButtons: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
  },
  addMoreRowsText: {
    fontSize: '12px',
  },
});
